import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, CheckCircle, Building2, Home, Square, CheckSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TRANSLATIONS = {
  misrad_hapanim: { en: 'Misrad Hapanim', he: 'משרד הפנים', fr: 'Ministère de l\'Intérieur', ar: 'وزارة الداخلية', ru: 'МВД Израиля' },
  municipality: { en: 'Municipality', he: 'עירייה', fr: 'Municipalité', ar: 'البلدية', ru: 'Муниципалитет' },
  water: { en: 'Water Company', he: 'חברת המים', fr: 'Compagnie d\'eau', ar: 'شركة المياه', ru: 'Водная компания' },
  electric: { en: 'Electric Corporation', he: 'חברת החשמל', fr: 'Compagnie d\'électricité', ar: 'شركة الكهرباء', ru: 'Электрическая компания' },
  bitouah_leumi: { en: 'Bitouah Leumi', he: 'ביטוח לאומי', fr: 'Bitouah Leumi', ar: 'التأمين الوطني', ru: 'Битуах Леуми' },
  tax: { en: 'Tax Authority', he: 'רשות המסים', fr: 'Autorité fiscale', ar: 'مصلحة الضرائب', ru: 'Налоговая служба' },
  post: { en: 'Post Office', he: 'דואר ישראל', fr: 'Bureau de poste', ar: 'مكتب البريد', ru: 'Почта Израиля' },
  schools: { en: 'Schools', he: 'בתי ספר', fr: 'Écoles', ar: 'المدارس', ru: 'Школы' },
  health: { en: 'Health Fund', he: 'קופת חולים', fr: 'Caisse maladie', ar: 'صندوق الصحة', ru: 'Больничная касса' },
  vaad: { en: 'Vaad Bayit', he: 'ועד בית', fr: 'Comité de l\'immeuble', ar: 'لجنة المبنى', ru: 'Домовой комитет' }
};

const ALL_ORGANIZATIONS = [
  { id: 'misrad_hapanim' },
  { id: 'municipality' },
  { id: 'water' },
  { id: 'electric' },
  { id: 'bitouah_leumi' },
  { id: 'tax' },
  { id: 'post' },
  { id: 'schools' },
  { id: 'health' },
  { id: 'vaad' }
];

function getOrgName(id, lang) {
  const t = ORG_TRANSLATIONS[id];
  if (!t) return id;
  return t[lang] || t['en'];
}

export default function AddressChangePage({ token }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentAddress, setCurrentAddress] = useState('');
  const [history, setHistory] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    postal_code: ''
  });

  useEffect(() => {
    fetchAddressData();
  }, []);

  const fetchAddressData = async () => {
    try {
      const [addressRes, historyRes] = await Promise.all([
        axios.get(`${API}/address/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/address/history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCurrentAddress(addressRes.data.current_address);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load address data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.street || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedOrgs.length === 0) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner au moins un organisme' : 'Please select at least one organization');
      return;
    }

    setSaving(true);
    
    const fullAddress = `${formData.street}, ${formData.city}${formData.postal_code ? ', ' + formData.postal_code : ''}`;
    
    try {
      const selectedOrgNames = ALL_ORGANIZATIONS
        .filter(o => selectedOrgs.includes(o.id))
        .map(o => o.name);

      const response = await axios.post(
        `${API}/address/change`,
        { new_address: fullAddress, organizations: selectedOrgNames },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('address_updated'));
      
      // Show success with organizations count
      toast.success(`${response.data.organizations_updated} ${t('organizations_updated')}`, {
        duration: 5000
      });
      
      // Reset form and reload data
      setFormData({ street: '', city: '', postal_code: '' });
      fetchAddressData();
    } catch (error) {
      toast.error('Failed to update address');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="address-change-title">
          {t('change_address')}
        </h1>
        <p className="text-lg text-slate-600">{t('propagated_to')}</p>
      </div>

      {/* Current Address Card */}
      {currentAddress && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Home className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">{t('current_address')}</p>
              <p className="text-xl font-bold text-slate-900" data-testid="current-address-display">
                {currentAddress}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Change Address Form */}
      <Card className="p-8 bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900">{t('new_address')}</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="street" className="text-base font-semibold">
              {t('address_line_1')} *
            </Label>
            <Input
              id="street"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              placeholder="123 Dizengoff Street"
              className="mt-2 h-12 rounded-xl"
              required
              data-testid="input-street"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="text-base font-semibold">
                {t('city')} *
              </Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Tel Aviv"
                className="mt-2 h-12 rounded-xl"
                required
                data-testid="input-city"
              />
            </div>

            <div>
              <Label htmlFor="postal_code" className="text-base font-semibold">
                {t('postal_code')}
              </Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder="6801234"
                className="mt-2 h-12 rounded-xl"
                data-testid="input-postal-code"
              />
            </div>
          </div>

          {/* Organizations List - Selectable */}
          <Card className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl">
            <div className="flex items-start gap-3 mb-4">
              <Building2 className="h-5 w-5 text-slate-600 mt-1" />
              <div className="w-full">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-slate-900">
                    {t('organizations_updated')}:
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedOrgs.length === ALL_ORGANIZATIONS.length) {
                        setSelectedOrgs([]);
                      } else {
                        setSelectedOrgs(ALL_ORGANIZATIONS.map(o => o.id));
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                    data-testid="toggle-all-orgs"
                  >
                    {selectedOrgs.length === ALL_ORGANIZATIONS.length
                      ? t('deselect_all') || (i18n.language === 'he' ? 'בטל הכל' : 'Deselect all')
                      : t('select_all') || (i18n.language === 'he' ? 'בחר הכל' : 'Select all')}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {selectedOrgs.length}/{ALL_ORGANIZATIONS.length} {t('selected') || (i18n.language === 'he' ? 'נבחרו' : 'selected')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {ALL_ORGANIZATIONS.map((org) => {
                    const isSelected = selectedOrgs.includes(org.id);
                    return (
                      <button
                        type="button"
                        key={org.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedOrgs(selectedOrgs.filter(id => id !== org.id));
                          } else {
                            setSelectedOrgs([...selectedOrgs, org.id]);
                          }
                        }}
                        className={`flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
                          isSelected
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-white border border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`org-${org.id}`}
                      >
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        ) : (
                          <Square className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${isSelected ? 'text-blue-800 font-medium' : 'text-slate-600'}`}>
                          {getOrgName(org.id, i18n.language)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>

          <Button
            type="submit"
            disabled={saving}
            className="w-full h-14 text-lg font-semibold rounded-full gradient-blue hover:opacity-90 transition-all shadow-lg shadow-blue-600/30"
            data-testid="submit-address-button"
          >
            {saving ? 'Updating...' : t('update_address')}
          </Button>
        </form>
      </Card>

      {/* Address History */}
      {history.length > 0 && (
        <Card className="p-8 bg-white rounded-2xl border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('address_history')}</h2>
          <div className="space-y-4">
            {history.map((change) => (
              <div
                key={change.id}
                className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                data-testid="address-history-item"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-900">{change.new_address}</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {t('changed_on')}: {new Date(change.change_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    {change.status}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {change.affected_organizations.length} {t('organizations_updated')}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
